import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  PlayCircle,
  MapPin,
  Briefcase,
  Clock,
  Globe2,
  Award as AwardIcon,
  ShieldCheck,
  FileText,
  Mail,
  CalendarClock,
  ExternalLink,
  MessageSquare,
} from "lucide-react";
import { Button } from "../components/ui/button";
import RecruiterActionModal from "../components/recruiter/RecruiterActionModal";
import Logo from "../components/Logo";
import BrandedAudioPlayer from "../components/BrandedAudioPlayer";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 1 recruiter-facing candidate profile page.
 *
 * Hierarchy (top → bottom):
 *   1. Header bar (Inter-View brand · "Candidate Profile" tag)
 *   2. Identity block (name + professional title / target role + city)
 *   3. Stitched interview video (no autoplay, PiP enabled)
 *   4. Snapshot facts (years exp · employment type · availability · relocate)
 *   5. Three key strengths
 *   6. Awards / Achievements (if any)
 *   7. Certifications / Licenses (if any)
 *   8. Languages (if any)
 *   9. CTAs: View Resume (PDF) + View Cover Letter (PDF)
 *  10. Recruiter Actions placeholder (Phase 2)
 *  11. Footer
 *
 * No public contact info (phone / email / linkedin) is rendered here.
 * Empty sections are hidden — never shown as "—" placeholders.
 */
export default function PublicVideoPage() {
  const { userId, packageId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalVariant, setModalVariant] = useState(null); // null | "interview" | "followups"

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = packageId
          ? `${API}/api/public/package/${packageId}`
          : `${API}/api/public/video/${userId}`;
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(
            res.status === 404
              ? (packageId
                ? "This application package link is no longer available."
                : "This candidate's profile is not available.")
              : "Could not load candidate profile."
          );
        }
        const payload = await res.json();
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, packageId]);

  const facts = useMemo(() => {
    if (!data) return [];
    const list = [];
    if (data.years_of_experience != null) {
      list.push({
        icon: Briefcase,
        label: "Experience",
        value: `${data.years_of_experience} ${
          data.years_of_experience === 1 ? "year" : "years"
        }`,
        testid: "recruiter-fact-experience",
      });
    }
    if (data.employment_preference) {
      list.push({
        icon: Clock,
        label: "Looking for",
        value: data.employment_preference,
        testid: "recruiter-fact-employment-preference",
      });
    }
    if (data.availability) {
      list.push({
        icon: CalendarClock,
        label: "Availability",
        value: data.availability,
        testid: "recruiter-fact-availability",
      });
    }
    if (data.willingness_to_relocate) {
      list.push({
        icon: MapPin,
        label: "Relocation",
        value: data.willingness_to_relocate,
        testid: "recruiter-fact-relocation",
      });
    }
    return list;
  }, [data]);

  return (
    <div
      className="min-h-screen bg-slate-50 text-slate-900"
      data-testid="public-video-page"
    >
      {/* ── Header ── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Logo height={26} priority />
          <span className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-[0.2em] font-semibold">
            Candidate Profile
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {loading && (
          <div
            className="flex items-center gap-3 justify-center py-20 text-slate-500"
            data-testid="public-video-loading"
          >
            <Loader2 className="animate-spin h-5 w-5" />
            Loading candidate profile…
          </div>
        )}

        {!loading && error && (
          <div
            className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center max-w-xl mx-auto"
            data-testid="public-video-error"
          >
            <AlertCircle className="h-10 w-10 text-rose-500 mx-auto mb-3" />
            <p className="text-rose-700 text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && data && (
          <div data-testid="public-video-content" className="space-y-10">
            {/* ── Identity ── */}
            <section data-testid="recruiter-identity">
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900"
                data-testid="recruiter-name"
              >
                {data.candidate_name}
              </h1>
              {(data.professional_title || data.target_role) && (
                <p
                  className="mt-2 text-blue-600 font-semibold text-sm sm:text-base uppercase tracking-[0.18em]"
                  data-testid="recruiter-title"
                >
                  {data.professional_title || data.target_role}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-500">
                {data.city_region && (
                  <span
                    className="inline-flex items-center gap-1.5"
                    data-testid="recruiter-location"
                  >
                    <MapPin className="h-4 w-4" />
                    {data.city_region}
                  </span>
                )}
                {data.duration_seconds != null && data.presentation_mode !== "hidden" && (
                  <span
                    className="inline-flex items-center gap-1.5"
                    data-testid="recruiter-video-duration"
                  >
                    <PlayCircle className="h-4 w-4" />
                    {Math.round(data.duration_seconds)}-second pre-recorded{" "}
                    {data.presentation_mode === "audio" ? "introduction" : "interview"}
                  </span>
                )}
              </div>
            </section>

            {/* ── Hero media surface (branches on presentation_mode) ── */}
            {data.presentation_mode === "audio" && data.intro_url && (
              <section data-testid="recruiter-audio-section">
                <BrandedAudioPlayer
                  src={data.intro_url}
                  durationSeconds={data.duration_seconds}
                  variant="hero"
                  data-testid="recruiter-audio-player"
                />
              </section>
            )}

            {data.presentation_mode !== "audio" &&
              data.presentation_mode !== "hidden" &&
              data.video_url && (
                <section
                  className="rounded-2xl overflow-hidden bg-slate-900 ring-1 ring-slate-200 shadow-xl shadow-slate-300/40"
                  data-testid="recruiter-video-section"
                >
                  <video
                    src={data.video_url}
                    controls
                    playsInline
                    preload="metadata"
                    controlsList="nodownload"
                    className="w-full aspect-video bg-black block"
                    data-testid="recruiter-video-player"
                  />
                </section>
              )}

            {/* ── Snapshot facts ── */}
            {facts.length > 0 && (
              <section
                className="grid grid-cols-2 md:grid-cols-4 gap-4"
                data-testid="recruiter-facts"
              >
                {facts.map((f) => (
                  <div
                    key={f.label}
                    className="rounded-xl bg-white border border-slate-200 px-4 py-3 shadow-sm shadow-slate-100"
                    data-testid={f.testid}
                  >
                    <div className="flex items-center gap-2 text-slate-400 text-[10px] uppercase tracking-[0.16em] font-semibold">
                      <f.icon className="h-3.5 w-3.5" />
                      {f.label}
                    </div>
                    <div className="mt-1 text-slate-900 font-semibold text-sm sm:text-base">
                      {f.value}
                    </div>
                  </div>
                ))}
              </section>
            )}

            {/* ── Key strengths ── */}
            {data.key_strengths && data.key_strengths.length > 0 && (
              <section data-testid="recruiter-key-strengths-section">
                <SectionHeader>Key strengths</SectionHeader>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {data.key_strengths.map((s, i) => (
                    <div
                      key={i}
                      className="rounded-xl bg-white border border-slate-200 p-5 shadow-sm shadow-slate-100"
                      data-testid={`recruiter-strength-card-${i}`}
                    >
                      <div className="text-blue-600 font-bold text-base">
                        {s.title}
                      </div>
                      {s.description && (
                        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                          {s.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── Availability details (free-form note) ── */}
            {data.availability_details && (
              <section data-testid="recruiter-availability-details-section">
                <SectionHeader>Schedule notes</SectionHeader>
                <p
                  className="mt-3 text-sm text-slate-700 leading-relaxed max-w-2xl"
                  data-testid="recruiter-availability-details"
                >
                  {data.availability_details}
                </p>
              </section>
            )}

            {/* ── Awards / Achievements ── */}
            {data.awards && data.awards.length > 0 && (
              <section data-testid="recruiter-awards-section">
                <SectionHeader icon={AwardIcon}>
                  Awards &amp; achievements
                </SectionHeader>
                <ul className="mt-4 space-y-3">
                  {data.awards.map((a, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm shadow-slate-100"
                      data-testid={`recruiter-award-${i}`}
                    >
                      <div className="mt-0.5 text-amber-500">
                        <AwardIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-900">
                          {a.title}
                        </div>
                        {(a.issuer || a.year) && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            {[a.issuer, a.year].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* ── Certifications ── */}
            {data.certifications && data.certifications.length > 0 && (
              <section data-testid="recruiter-certifications-section">
                <SectionHeader icon={ShieldCheck}>
                  Certifications &amp; licenses
                </SectionHeader>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.certifications.map((c, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm shadow-slate-100"
                      data-testid={`recruiter-certification-${i}`}
                    >
                      <div className="mt-0.5 text-emerald-500">
                        <ShieldCheck className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-900">
                          {c.name}
                        </div>
                        {(c.issuer || c.year) && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            {[c.issuer, c.year].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* ── Languages ── */}
            {data.languages_spoken && data.languages_spoken.length > 0 && (
              <section data-testid="recruiter-languages-section">
                <SectionHeader icon={Globe2}>Languages</SectionHeader>
                <div className="mt-3 flex flex-wrap gap-2">
                  {data.languages_spoken.map((l, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-100"
                      data-testid={`recruiter-language-${i}`}
                    >
                      {l}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* ── Document CTAs ── */}
            {(data.has_resume || data.has_cover_letter) && (
              <section
                className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm shadow-slate-100"
                data-testid="recruiter-documents-section"
              >
                <SectionHeader icon={FileText}>Documents</SectionHeader>
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  {data.has_resume && (
                    <Button
                      asChild
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="recruiter-view-resume-btn"
                    >
                      <a
                        href={packageId
                          ? `${API}/api/public/package/${packageId}/resume.pdf`
                          : `${API}/api/public/resume/${userId}.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        View Resume (PDF)
                        <ExternalLink className="h-3.5 w-3.5 ml-2 opacity-70" />
                      </a>
                    </Button>
                  )}
                  {data.has_cover_letter && (
                    <Button
                      asChild
                      variant="outline"
                      className="border-slate-300 text-slate-700 hover:bg-slate-100"
                      data-testid="recruiter-view-cover-letter-btn"
                    >
                      <a
                        href={packageId
                          ? `${API}/api/public/package/${packageId}/cover-letter.pdf`
                          : `${API}/api/public/cover-letter/${userId}.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        View Cover Letter (PDF)
                        <ExternalLink className="h-3.5 w-3.5 ml-2 opacity-70" />
                      </a>
                    </Button>
                  )}
                </div>
              </section>
            )}

            {/* ── Recruiter Actions ── */}
            <section data-testid="recruiter-actions-section">
              <SectionHeader icon={Mail}>Recruiter actions</SectionHeader>
              <div
                className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm shadow-slate-100"
                data-testid="recruiter-actions-card"
              >
                <p className="text-sm text-slate-600">
                  Want to reach{" "}
                  <span className="font-semibold text-slate-900">
                    {data.candidate_name}
                  </span>
                  ? Inter-View routes your message — their personal contact info
                  stays private.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => setModalVariant("interview")}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="recruiter-request-interview-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Request Interview
                  </Button>
                  <Button
                    onClick={() => setModalVariant("followups")}
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100"
                    data-testid="recruiter-ask-followups-btn"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Ask Follow-Up Questions
                  </Button>
                </div>
              </div>
            </section>

            <RecruiterActionModal
              open={modalVariant !== null}
              variant={modalVariant}
              userId={userId}
              candidateName={data.candidate_name}
              onClose={() => setModalVariant(null)}
            />

            {/* ── Footer ── */}
            <footer
              className="pt-4 border-t border-slate-200 text-center text-xs text-slate-500"
              data-testid="recruiter-footer"
            >
              Verified video interviews. Smarter hiring. ·{" "}
              <span className="text-blue-600 font-semibold">Inter-View.ca</span>
            </footer>
          </div>
        )}
      </main>
    </div>
  );
}

function SectionHeader({ children, icon: Icon }) {
  return (
    <h2 className="flex items-center gap-2 text-slate-900 font-bold text-lg">
      {Icon && <Icon className="h-4 w-4 text-blue-600" />}
      {children}
    </h2>
  );
}
